/**
 * Générateur — Automatisme : donner la nature d'une face d'une pyramide
 * représentée en perspective cavalière.
 * Attendu ID : 1239 | Niveau : 3ème | Domaine : Espace et géométrie,
 * Représentation de l'espace
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 l. 1074 ; la base omet
 * « représentée » : le mot impose la FIGURE, toujours présente ici).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * EXTRACTION de gen_185 nature_qcm_unique (objectif 4e) RETOUCHÉE : une
 * pyramide à sommets nommés (pyramide.js, lettres saines), base carrée ou
 * rectangulaire, UNE face nommée par question. Jamais « triangle » et
 * « triangle isocèle » ensemble (isocèle = 4e, À ARBITRER) ; la base
 * triangulaire est hors plage, réservée à la fiche (pas de sacrifice de
 * lettres). Pas de saisie (la nature se choisit).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « La nature d'une face » (nature_qcm_unique) : « SABCD est une
 *     pyramide à base carrée. Quelle est la nature de la face SAB ? »
 *     [un triangle ; un parallélogramme ; un rectangle ; un trapèze] ; la
 *     base une fois sur trois [un carré ; un parallélogramme ; un rectangle ;
 *     un triangle] (« la plus précise » : les inclusions vraies ne sont pas
 *     défendables, comme en 1019).
 *   Carte 2 « Est-ce une face ? » (face_qcm_unique) : « Lequel de ces
 *     triangles est une face de la pyramide ? » [SAB ; SAC ; SBD ; ABD].
 *   Carte 3 « Vrai ou faux » (nature_tableau_vf) : quatre lignes (une face
 *     latérale, la base, un faux triangle, un compte).
 *
 * INVARIANTS : lettres par pickLettresSains ; la figure porte data-solide
 * (sommet, base ordonnée) ; au V/F, jamais une inclusion vraie servie comme
 * fausse (« la base est un parallélogramme » pour un carré). Pas de
 * sacrifice (fiche sur une base triangulaire).
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   perspective_lue        : parallélogramme, losange, trapèze (la forme vue au lieu de la forme vraie), PRINCIPALE ;
 *   face_de_l_autre_sorte  : rectangle pour une face latérale, triangle pour la base ;
 *   moins_precis           : rectangle pour la base carrée (vrai, mais pas le plus précis) ;
 *   forme_voisine          : carré pour une base rectangulaire ;
 *   pas_une_face           : SAC, SBD (une section diagonale), ABD (dans la base) ;
 *   compte                 : cinq faces triangulaires pour quatre.
 */
import {
    randomChoice, randInt, shuffle, pickLettresSains,
    makeQcm, makeVraiFaux,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { svgPyramideNommee, NOM_BASE } from './pyramide';

const ATTENDU_ID = 1239;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_NATURE = 'La nature d\'une face';
const L_FACE = 'Est-ce une face ?';
const L_VF = 'Vrai ou faux';

const POOL = 'ABCDEFGHJKLMNPRSTUV'.split('');
const EXPLICATIONS = {
    regle: 'Les faces latérales d\'une pyramide sont des triangles (un côté de la base et le sommet) ; la base est le polygone du bas, un carré ou un rectangle.',
    perspective_lue: 'En perspective cavalière, la base est dessinée penchée : elle a l\'air d\'un parallélogramme, mais sa vraie forme est un carré ou un rectangle.',
    face_de_l_autre_sorte: 'Une face latérale rejoint le sommet : c\'est un triangle. Seule la base est un carré ou un rectangle.',
    pas_une_face: 'SAC traverse la pyramide (A et C sont opposés) : ce n\'est pas une face. Une face latérale, c\'est le sommet et un côté de la base.',
    compte: 'Une pyramide à base carrée a 4 faces triangulaires (une par côté de la base) et une base : 5 faces.',
};

/** Une pyramide nommée : sommet + quatre lettres de base, base carrée ou rectangulaire. */
const tirer = () => {
    const base = randInt(1, 3) === 1 ? 'rectangle' : 'carre';
    const [sommet, ...lettres] = pickLettresSains(POOL, 5);
    return { base, sommet, lettres, nom: `${sommet}${lettres.join('')}` };
};
const figure = (py) => imgElement(svgPyramideNommee(py), 'large');
const intro = (py) => `<p>${py.nom} est une pyramide à base ${NOM_BASE[py.base]}.</p>`;
const faceLaterale = (py, i) => `${py.sommet}${py.lettres[i]}${py.lettres[(i + 1) % 4]}`;
const baseNom = (py) => py.lettres.join('');
const natureBase = (py) => (py.base === 'carre' ? 'un carré' : 'un rectangle');

// ─── Carte 1 : la nature d'une face (QCM) ─────────────────────────────────

const genNature = () => {
    const py = tirer();
    const surBase = randInt(1, 3) === 1;
    let face; let bon; let faux;
    if (surBase) {
        face = baseNom(py); bon = natureBase(py);
        faux = [{ v: 'un parallélogramme', code: 'perspective_lue' }, py.base === 'carre' ? { v: 'un rectangle', code: 'moins_precis' } : { v: 'un carré', code: 'forme_voisine' }, { v: 'un triangle', code: 'face_de_l_autre_sorte' }];
    } else {
        face = faceLaterale(py, randInt(0, 3)); bon = 'un triangle';
        faux = [{ v: 'un parallélogramme', code: 'perspective_lue' }, { v: natureBase(py), code: 'face_de_l_autre_sorte' }, { v: 'un trapèze', code: 'perspective_lue' }];
    }
    const payload = makeQcm([bon, ...faux.map((f) => f.v)], bon, `${intro(py)}<p><b>Quelle est la nature la plus précise de la face ${face} ?</b></p>`, { ...opts(L_NATURE), elements_additionnels: figure(py) });
    payload.contenu = { ...payload.contenu, sousType: surBase ? 'base' : 'laterale', explications: EXPLICATIONS };
    payload._meta.params = { ...py, face, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 2 : est-ce une face ? (QCM) ────────────────────────────────────

const genFace = () => {
    const py = tirer();
    const [a, b, c, d] = py.lettres; const S = py.sommet;
    const bon = faceLaterale(py, randInt(0, 3));
    const faux = shuffle([{ v: `${S}${a}${c}`, code: 'pas_une_face' }, { v: `${S}${b}${d}`, code: 'pas_une_face' }, { v: randomChoice([`${a}${b}${d}`, `${a}${b}${c}`, `${b}${c}${d}`]), code: 'pas_une_face' }]);
    const payload = makeQcm([bon, ...faux.map((f) => f.v)], bon, `${intro(py)}<p><b>Lequel de ces triangles est une face de la pyramide ?</b></p>`, { ...opts(L_FACE), elements_additionnels: figure(py) });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...py, face: bon, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 3 : Vrai ou faux ───────────────────────────────────────────────

const genVf = () => {
    const py = tirer();
    const [a, , c] = py.lettres; const S = py.sommet;
    const lat = faceLaterale(py, randInt(0, 3)); const lat2 = faceLaterale(py, randInt(0, 3));
    const candidats = [
        { l: `La face ${lat} est un triangle.`, v: true, fam: 'laterale' },
        { l: `La face ${lat2} est ${randomChoice(['un rectangle', 'un parallélogramme'])}.`, v: false, fam: 'laterale', code: 'perspective_lue' },
        { l: `La base ${baseNom(py)} est ${natureBase(py)}.`, v: true, fam: 'base' },
        py.base === 'carre'
            ? { l: `La base ${baseNom(py)} est un triangle.`, v: false, fam: 'base', code: 'face_de_l_autre_sorte' }
            : { l: `La base ${baseNom(py)} est un carré.`, v: false, fam: 'base', code: 'forme_voisine' },
        { l: `${S}${a}${c} est une face de la pyramide.`, v: false, fam: 'face', code: 'pas_une_face' },
        { l: `${lat} est une face de la pyramide.`, v: true, fam: 'face' },
        { l: 'Cette pyramide a 4 faces triangulaires.', v: true, fam: 'compte' },
        { l: `Cette pyramide a ${randomChoice([3, 5])} faces triangulaires.`, v: false, fam: 'compte', code: 'compte' },
    ];
    for (let essai = 0; essai < 100; essai++) {
        const nbFaux = randInt(1, 3);
        const fausses = shuffle(candidats.filter((x) => !x.v)); const vraies = shuffle(candidats.filter((x) => x.v));
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((x) => x.fam)).size !== 4) continue;
        const payload = makeVraiFaux(lignes.map((x) => x.l), lignes.map((x) => (x.v ? 0 : 1)), `${intro(py)}<p><b>Vrai ou faux ?</b></p>`, { ...opts(L_VF), elements_additionnels: figure(py) });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...py, distracteurs: lignes.filter((x) => x.code).map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_1239 : V/F sans tirage');
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'pyramide à sommets nommés (lettres saines), base carrée (deux fois sur trois) ou rectangulaire, figure toujours présente',
        'jamais « triangle isocèle » à côté de « triangle » ; jamais une inclusion vraie (carré → parallélogramme) servie comme fausse',
    ],
    nature_qcm_unique: 'perspective_lue (parallélogramme, trapèze : la forme vue), face_de_l_autre_sorte (rectangle pour une face latérale, triangle pour la base), moins_precis (rectangle pour un carré), forme_voisine (carré pour un rectangle)',
    face_qcm_unique: 'pas_une_face : deux sections diagonales (SAC, SBD) et un triangle dans la base (ABD)',
    nature_tableau_vf: 'quatre familles exclusives : face latérale, base, est-ce une face, compte des faces triangulaires',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    nature_qcm_unique: genNature,
    face_qcm_unique: genFace,
    nature_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'nature', label: L_NATURE, formatsList: [{ key: 'nature_qcm_unique', label: 'QCM' }, { key: 'nature_tableau_vf', label: 'Vrai/Faux' }] },
    { id: 'face', label: L_FACE, formatsList: [{ key: 'face_qcm_unique', label: 'QCM' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
